import { API_KEY_ACTOR_PREFIX } from "./api-keys";
import { DOCUMENT_FILING } from "./document-filing";
import { DOCUMENT_LINKER } from "./document-links";
import type { AuditChange, AuditRun } from "./organizations";
import { DIRECTORY_AGENT } from "./work";

// What Ravnar's automation does on its own, as the audit log records it: every run of the three agents of Intake — the
// Directory agent, the Document linker and Document Filing — with the tokens it took, finished or failed, and Bedrock
// Data Automation's read of each document. The API, the agents and the frontend share these.

/** Who an entry names for Bedrock Data Automation, which reads each Intake document into Markdown before any agent. */
export const BEDROCK_DATA_AUTOMATION = "Bedrock Data Automation";

/** A count of tokens as the audit log writes it: "14,212". */
export function formatTokenCount(tokens: number): string {
	return tokens.toLocaleString("en-US");
}

/** How long a run took, as the audit log writes it: "under 1 s", "38 s", "2 min", "2 min 10 s". */
export function formatRunDuration(milliseconds: number): string {
	const seconds = Math.round(milliseconds / 1000);
	if (seconds < 1) {
		return "under 1 s";
	}
	if (seconds < 60) {
		return `${String(seconds)} s`;
	}
	const minutes = Math.floor(seconds / 60);
	const rest = seconds % 60;
	return rest === 0 ? `${String(minutes)} min` : `${String(minutes)} min ${String(rest)} s`;
}

/** An audit entry of an automated run, but for its actor and its category. */
export interface RunAuditEvent {
	readonly action: string;
	readonly detail: string;
	readonly changes: AuditChange[];
	readonly run: AuditRun;
}

/** What one agent's run on a document was, as its audit entry says it. */
export interface AgentRunFacts {
	/** The document the agent ran on, by its name in Intake. */
	readonly fileName: string;
	/** What a finished run did, in a few words ("2 parties added"); undefined for a run that failed. */
	readonly outcome: string | undefined;
	readonly startedAt: Date;
	readonly finishedAt: Date;
	/** How many times the model answered. */
	readonly turns: number;
	readonly inputTokens: number;
	readonly outputTokens: number;
	/**
	 * The model it ran on, as its provider names it: `claude-haiku-5-5` on Anthropic's API; a model's own id, or an
	 * inference profile's, on Bedrock.
	 */
	readonly model: string;
}

/**
 * The audit entry of one agent's run on a document: "Agent run finished" / "invoice.pdf · 2 parties added · 14,212
 * tokens · 38 s", or "Agent run failed" / "invoice.pdf · stopped after 14 turns · 61,400 tokens · 2 min 10 s". Its values
 * say what the run did and took, and `run` holds the same in numbers. A failed run's tokens are spent all the same.
 */
export function agentRunAudit({
	fileName,
	outcome,
	startedAt,
	finishedAt,
	turns,
	inputTokens,
	outputTokens,
	model,
}: AgentRunFacts): RunAuditEvent {
	const took = `${formatTokenCount(inputTokens + outputTokens)} tokens · ${formatRunDuration(finishedAt.getTime() - startedAt.getTime())}`;
	const stopped = `stopped after ${String(turns)} ${turns === 1 ? "turn" : "turns"}`;
	return {
		action: outcome === undefined ? "Agent run failed" : "Agent run finished",
		detail: `${fileName} · ${outcome ?? stopped} · ${took}`,
		changes: [
			{ label: "Document", value: fileName },
			{ label: "Outcome", value: outcome ?? "Failed" },
			{ label: "Turns", value: String(turns) },
			{ label: "Input tokens", value: formatTokenCount(inputTokens) },
			{ label: "Output tokens", value: formatTokenCount(outputTokens) },
			{ label: "Model", value: model },
		],
		run: {
			startedAt: startedAt.toISOString(),
			finishedAt: finishedAt.toISOString(),
			outcome: outcome === undefined ? "failed" : "finished",
			turns,
			inputTokens,
			outputTokens,
		},
	};
}

/** What a finished run of the Directory agent did: "No party added", "1 party added", "2 parties added". */
export function directoryReviewOutcome(added: number): string {
	if (added === 0) {
		return "No party added";
	}
	return `${String(added)} ${added === 1 ? "party" : "parties"} added`;
}

/** What a finished run of the Document linker did: "Linked to no party", "Linked to 1 party", "Linked to 3 parties". */
export function documentLinkingOutcome(linked: number): string {
	if (linked === 0) {
		return "Linked to no party";
	}
	return `Linked to ${String(linked)} ${linked === 1 ? "party" : "parties"}`;
}

/**
 * The audit entry of Bedrock Data Automation's read of a document: "Document read" / "invoice.pdf · 3 pages", or just
 * the name when Bedrock does not say how many pages it read. It reads by the page and spends no AI tokens.
 */
export function documentReadAudit(fileName: string, pages: number | undefined, finishedAt: Date): RunAuditEvent {
	const read = pages === undefined ? "" : ` · ${String(pages)} ${pages === 1 ? "page" : "pages"}`;
	return {
		action: "Document read",
		detail: `${fileName}${read}`,
		changes: [
			{ label: "Document", value: fileName },
			...(pages === undefined ? [] : [{ label: "Pages", value: String(pages) }]),
		],
		run: {
			finishedAt: finishedAt.toISOString(),
			outcome: "finished",
			...(pages !== undefined && { pages }),
		},
	};
}

/** The audit entry of a read Bedrock Data Automation could not finish: "Document could not be read" / "invoice.pdf". */
export function documentNotReadAudit(fileName: string, finishedAt: Date): RunAuditEvent {
	return {
		action: "Document could not be read",
		detail: fileName,
		changes: [{ label: "Document", value: fileName }],
		run: { finishedAt: finishedAt.toISOString(), outcome: "failed" },
	};
}

/**
 * Who did what an audit entry records, as the Audit log page tells them apart: a person, one of Intake's three agents,
 * Ravnar's own automation — Bedrock Data Automation's reads, Reconciliation — or software with an API key.
 */
export type AuditActorKind = "person" | "agent" | "automation" | "api-key";

/** The three agents of Intake, as their audit entries name them. */
const INTAKE_AGENTS: ReadonlySet<string> = new Set([DIRECTORY_AGENT, DOCUMENT_LINKER, DOCUMENT_FILING]);

/**
 * What an entry's actor is, from how its producer names it: `apiKeyActor`'s "API key …" is software's, an email is a
 * person's, the three agents are named as `DIRECTORY_AGENT`, `DOCUMENT_LINKER` and `DOCUMENT_FILING` name them, and any
 * other name is Ravnar's own automation. A key's name may hold an @, so the key is told apart first.
 */
export function auditActorKind(actor: string): AuditActorKind {
	if (actor.startsWith(API_KEY_ACTOR_PREFIX)) {
		return "api-key";
	}
	if (actor.includes("@")) {
		return "person";
	}
	return INTAKE_AGENTS.has(actor) ? "agent" : "automation";
}
